import {AssaysFeature, Capability, RecipesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';

/** This is a list of features that are common between procedures types.
 *  Before adding a new feature type, make sure they are not in the UNMAPPED_FEATURES for the capabilities.
 *  UNMAPPED_FEATURES can be found in the model_to_capability.py file for the procedureType.
 *  e.g, benchling/auth/capabilities/components/recipes/model_to_capability.py: UNMAPPED_FEATURES
 */
export enum ProceduresFeature {
  ADMIN = 'ADMIN',
  ARCHIVE = 'ARCHIVE',
  CREATE = 'CREATE',
  CREATE_ANALYSIS = 'CREATE_ANALYSIS',
  CREATE_STUDY = 'CREATE_STUDY',
  EDIT_CONFIG = 'EDIT_CONFIG',
  EXECUTE_EXPERIMENT = 'EXECUTE_EXPERIMENT',
  EXPORT = 'EXPORT',
  MOVE = 'MOVE',
  OPEN = 'OPEN',
  PLAN_EXPERIMENT = 'PLAN_EXPERIMENT',
  UNARCHIVE = 'UNARCHIVE',
}

// This is a mapping of ProcedureFeature to the corresponding AssaysFeature or RecipesFeature.
// Exported for testing only
export const ProcedureFeaturesByFeatureType: {
  [key in ProceduresFeature]: {[key in ProcedureType]: AssaysFeature | RecipesFeature};
} = {
  ADMIN: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_ADMIN,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_ADMIN,
  },
  ARCHIVE: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_ARCHIVE,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_ARCHIVE,
  },
  CREATE: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_CREATE_ASSAY,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_CREATE_RECIPE,
  },
  CREATE_ANALYSIS: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_CREATE_ANALYSIS,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_CREATE_ANALYSIS,
  },
  CREATE_STUDY: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_CREATE_STUDY,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_CREATE_STUDY,
  },
  EDIT_CONFIG: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_EDIT_CONFIG,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_EDIT_CONFIG,
  },
  EXECUTE_EXPERIMENT: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_EXECUTE_EXPERIMENT,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_EXECUTE_EXPERIMENT,
  },
  EXPORT: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_EXPORT,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_EXPORT,
  },
  MOVE: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_MOVE,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_MOVE,
  },
  OPEN: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_OPEN,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_OPEN,
  },
  PLAN_EXPERIMENT: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_PLAN_EXPERIMENT,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_PLAN_EXPERIMENT,
  },
  UNARCHIVE: {
    [ProcedureType.ASSAY]: AssaysFeature.ASSAYS_UNARCHIVE,
    [ProcedureType.RECIPE]: RecipesFeature.RECIPES_UNARCHIVE,
  },
};

export const PROCEDURE_TYPE_TO_CAPABILITY: Record<ProcedureType, Capability> = {
  [ProcedureType.ASSAY]: Capability.ASSAYS,
  [ProcedureType.RECIPE]: Capability.RECIPES,
};

/** Checks if a feature is enabled for a given procedure type.
 *
 * @param procedureType type of procedure
 * @param feature ProcedureFeature to check if it is enabled
 * @returns boolean indicating if the feature is enabled for the procedure type
 */
export const isFeatureEnabledForProcedureType = (
  procedureType: ProcedureType,
  feature: ProceduresFeature
): boolean =>
  (procedureType === ProcedureType.ASSAY &&
    isFeatureEnabled({
      feature: ProcedureFeaturesByFeatureType[feature][procedureType],
    })) ||
  (procedureType === ProcedureType.RECIPE &&
    isFeatureEnabled({feature: ProcedureFeaturesByFeatureType[feature][procedureType]}));
